/**
 * BLE scan and session state for on-site OutdoorLink provisioning.
 *
 * Holds per-ICCID discovery results, tracks the currently connected+authenticated
 * unit, and drives the auth state machine (connecting → authenticating →
 * authenticated / failed). Wraps the entire app so provisioning UI anywhere in
 * the tree can reach BLE state without prop-drilling.
 *
 * On web (and any platform where BLE is unavailable), all actions degrade
 * gracefully — the provider still renders its children and surfaces
 * authStatus='failed' instead of throwing and crashing the app.
 *
 * Contains:
 * - BluetoothContext    — the React context object (exported for the useBluetooth hook)
 * - BluetoothProvider  — the provider component (default-exported wraps the app)
 * - useBluetooth       — consumer hook; throws outside a provider
 */

import { Capacitor } from '@capacitor/core';
import { BleClient } from '@capacitor-community/bluetooth-le';
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { DEBUG_BLUETOOTH } from '@sophocles/features/bluetooth/bluetoothConfig';
import {
  authenticateDevice,
  connectToDevice,
  disconnectFromDevice,
  sendRelayCommand as sendRelayCommandToDevice,
} from '@sophocles/features/bluetooth/connectionService';
import { iccidMatchesSite, parseAdvertisedIccid } from '@sophocles/features/bluetooth/iccid';
import { startBleScan, stopBleScan } from '@sophocles/features/bluetooth/scanService';

export const BluetoothContext = createContext(undefined);

/**
 * Provider that wraps the app and holds all BLE scan / session state.
 *
 * Degrades to no-op actions on web or when the BLE plugin is unavailable.
 * BleClient.initialize() is called lazily before the first scan so the OS
 * permission prompt does not fire at app boot.
 *
 * @param {{ children: React.ReactNode }} props
 */
export const BluetoothProvider = ({ children }) => {
  const isNative = Capacitor.isNativePlatform();

  // Whether BleClient.initialize() has already succeeded this session.
  const initializedRef = useRef(false);

  // --- Scan state ---
  const [isScanning, setIsScanning] = useState(false);

  /** @type {[Map<string, {deviceId: string, rssi: number, name: string|null}>, Function]} */
  const [discoveredUnits, setDiscoveredUnits] = useState(new Map());

  // Mirror of discoveredUnits for use inside the scan callback — avoids
  // stale closures because the callback is captured once per startScan call.
  const discoveredUnitsRef = useRef(new Map());

  // scanningRef lets stopScan / cleanup guard against double-stop without
  // depending on isScanning state (which lags a render cycle).
  const scanningRef = useRef(false);

  // --- Connection / auth state ---
  const [connectedIccid, setConnectedIccid] = useState(null);
  const [authStatus, setAuthStatus] = useState('idle');
  const [connectingIccid, setConnectingIccid] = useState(null);

  // The deviceId of the active (or in-flight) connection — the single source of
  // truth for teardown. Tracked here, NOT derived from discoveredUnits, because
  // a re-scan clears that map and we'd lose the handle needed to actually close
  // the OS-level BLE link. Also acts as the cancellation token: the connect flow
  // bails if this no longer points at its deviceId. null when idle.
  const activeDeviceIdRef = useRef(null);

  // ----- helpers -----

  /** Ensure the BLE plugin is initialized. No-op after first success. */
  const ensureInitialized = useCallback(async () => {
    if (initializedRef.current) return;

    await BleClient.initialize();
    initializedRef.current = true;

    if (DEBUG_BLUETOOTH) {
      console.debug('[BLE] BleClient initialized');
    }
  }, []);

  // ----- public actions -----

  /**
   * Begin a BLE scan. For each ScanResult that carries an OutdoorLink ICCID
   * in manufacturerData, upserts the unit into discoveredUnits.
   *
   * Safe to call on web — degrades by logging and returning early.
   */
  const startScan = useCallback(async () => {
    if (!isNative) {
      if (DEBUG_BLUETOOTH) {
        console.debug('[BLE] startScan: not a native platform, skipping');
      }
      return;
    }

    if (scanningRef.current) {
      if (DEBUG_BLUETOOTH) {
        console.debug('[BLE] startScan: scan already in progress');
      }
      return;
    }

    try {
      await ensureInitialized();

      // Reset discovered units for the new scan.
      const fresh = new Map();
      discoveredUnitsRef.current = fresh;
      setDiscoveredUnits(fresh);

      setIsScanning(true);
      scanningRef.current = true;

      await startBleScan(result => {
        const iccid = parseAdvertisedIccid(result.manufacturerData);
        if (!iccid) return;

        const entry = {
          deviceId: result.device.deviceId,
          rssi: result.rssi,
          name: result.device.name ?? null,
        };

        // Build a new Map — never mutate the existing one.
        const next = new Map(discoveredUnitsRef.current);
        next.set(iccid, entry);
        discoveredUnitsRef.current = next;
        setDiscoveredUnits(next);

        if (DEBUG_BLUETOOTH) {
          console.debug('[BLE] detected unit', iccid, 'rssi', result.rssi);
        }
      });
    } catch (err) {
      console.error('[BLE] startScan error:', err);
      setIsScanning(false);
      scanningRef.current = false;
    }
  }, [isNative, ensureInitialized]);

  /**
   * Stop the active BLE scan.
   */
  const stopScan = useCallback(async () => {
    if (!scanningRef.current) return;

    try {
      await stopBleScan();
    } catch (err) {
      console.error('[BLE] stopScan error:', err);
    } finally {
      setIsScanning(false);
      scanningRef.current = false;
    }
  }, []);

  /** Best-effort close of the OS-level BLE link for a deviceId. No-op if null. */
  const tearDownDevice = useCallback(async deviceId => {
    if (!deviceId) return;
    try {
      await disconnectFromDevice(deviceId);
      if (DEBUG_BLUETOOTH) {
        console.debug('[BLE] disconnectFromDevice resolved for', deviceId);
      }
    } catch (err) {
      console.error('[BLE] disconnect error:', err);
    }
  }, []);

  /**
   * Connect to the unit identified by `iccid` and run the PSK handshake.
   *
   * Drives authStatus: connecting → authenticating → authenticated | failed.
   * On success, sets connectedIccid. On hardware disconnect, resets to idle.
   *
   * @param {string} iccid
   */
  const connectByIccid = useCallback(
    async iccid => {
      if (!isNative) {
        if (DEBUG_BLUETOOTH) {
          console.debug('[BLE] connectByIccid: not a native platform, skipping');
        }
        setConnectingIccid(null);
        setAuthStatus('failed');
        return;
      }

      const unit = discoveredUnitsRef.current.get(iccid);
      if (!unit) {
        console.warn('[BLE] connectByIccid: no discovered unit for iccid', iccid);
        setConnectingIccid(null);
        setAuthStatus('failed');
        return;
      }

      const { deviceId } = unit;
      // Mark this as the active attempt; teardown + cancellation key off it.
      activeDeviceIdRef.current = deviceId;

      try {
        setConnectingIccid(iccid);
        setAuthStatus('connecting');

        if (DEBUG_BLUETOOTH) {
          console.debug('[BLE] connecting to unit', iccid, deviceId);
        }

        const onDisconnect = () => {
          if (DEBUG_BLUETOOTH) {
            console.debug('[BLE] device disconnected', iccid);
          }
          activeDeviceIdRef.current = null;
          setConnectingIccid(null);
          setConnectedIccid(null);
          setAuthStatus('idle');
        };

        await connectToDevice(deviceId, onDisconnect);

        // Cancelled (a disconnect/leave or a newer connect) while connecting?
        // Close the link we just opened and bail without claiming connected.
        if (activeDeviceIdRef.current !== deviceId) {
          await tearDownDevice(deviceId);
          return;
        }

        setAuthStatus('authenticating');

        const result = await authenticateDevice(deviceId);

        if (activeDeviceIdRef.current !== deviceId) {
          await tearDownDevice(deviceId);
          return;
        }

        if (result.success) {
          setConnectingIccid(null);
          setConnectedIccid(iccid);
          setAuthStatus('authenticated');
          if (DEBUG_BLUETOOTH) {
            console.debug('[BLE] connected + authenticated to site unit', iccid);
          }
        } else {
          console.warn('[BLE] authentication failed', iccid, result.error);
          // connectToDevice succeeded but auth didn't — close the open link.
          await tearDownDevice(deviceId);
          activeDeviceIdRef.current = null;
          setConnectingIccid(null);
          setAuthStatus('failed');
        }
      } catch (err) {
        console.error('[BLE] connectByIccid error:', err);
        await tearDownDevice(deviceId);
        if (activeDeviceIdRef.current === deviceId) {
          activeDeviceIdRef.current = null;
        }
        setConnectingIccid(null);
        setAuthStatus('failed');
      }
    },
    [isNative, tearDownDevice],
  );

  /**
   * Disconnect from the currently connected unit.
   */
  const disconnect = useCallback(async () => {
    // Capture + clear the active deviceId first: this also cancels any in-flight
    // connect (its post-await guard sees the ref no longer matches and bails).
    const deviceId = activeDeviceIdRef.current;
    activeDeviceIdRef.current = null;

    if (DEBUG_BLUETOOTH) {
      console.debug('[BLE] disconnect requested; active deviceId:', deviceId ?? '(none)');
    }

    setConnectingIccid(null);
    setConnectedIccid(null);
    setAuthStatus('idle');

    await tearDownDevice(deviceId);
  }, [tearDownDevice]);

  /**
   * Returns true when the given ICCID is the currently connected+authenticated unit.
   *
   * @param {string} iccid
   * @returns {boolean}
   */
  const isConnected = useCallback(iccid => connectedIccid === iccid, [connectedIccid]);

  /**
   * Returns true when there is an active authenticated BLE connection to the unit
   * whose ICCID corresponds to `siteIccid`.
   *
   * Uses the tolerant iccidMatchesSite check rather than exact equality because
   * connectedIccid is the advertised (reconstructed) ICCID while siteIccid is
   * the value stored on the site record — the two may differ by prefix.
   *
   * @param {string | null | undefined} siteIccid
   * @returns {boolean}
   */
  const isConnectedToSite = useCallback(
    siteIccid => connectedIccid != null && iccidMatchesSite(connectedIccid, siteIccid),
    [connectedIccid],
  );

  /**
   * Returns true when a unit matching `siteIccid` is currently advertising
   * (present in the discovered-units map). Tolerant match, mirroring the
   * auto-connect selector. Depends on discoveredUnits so consumers re-evaluate
   * as units come and go.
   *
   * @param {string | null | undefined} siteIccid
   * @returns {boolean}
   */
  const isSiteAdvertising = useCallback(
    siteIccid => {
      if (!siteIccid) {
        return false;
      }
      for (const advertisedIccid of discoveredUnits.keys()) {
        if (iccidMatchesSite(advertisedIccid, siteIccid)) {
          return true;
        }
      }
      return false;
    },
    [discoveredUnits],
  );

  /**
   * Send a relay toggle command to the currently connected unit over BLE.
   *
   * Resolves the connected unit's deviceId from discoveredUnitsRef and calls
   * the UART write. Returns a failure result if no unit is connected.
   *
   * @param {number} relayNumber - Physical relay number from the site data.
   * @returns {Promise<{ success: boolean, error?: string }>}
   */
  const sendRelayCommand = useCallback(
    async relayNumber => {
      // Use the tracked active deviceId, not a discoveredUnits lookup — the scan
      // map can be cleared while still connected.
      const deviceId = activeDeviceIdRef.current;
      if (!connectedIccid || !deviceId) {
        return { success: false, error: 'No BLE unit connected' };
      }
      if (DEBUG_BLUETOOTH) {
        console.debug('[BLE] sendRelayCommand relay', relayNumber, 'to', deviceId);
      }
      return sendRelayCommandToDevice(deviceId, relayNumber);
    },
    [connectedIccid],
  );

  // Memoize the context value so consumers only re-render when
  // the pieces they care about actually change.
  const value = useMemo(
    () => ({
      isScanning,
      discoveredUnits,
      connectedIccid,
      connectingIccid,
      authStatus,
      startScan,
      stopScan,
      connectByIccid,
      disconnect,
      isConnected,
      isConnectedToSite,
      isSiteAdvertising,
      sendRelayCommand,
    }),
    [
      isScanning,
      discoveredUnits,
      connectedIccid,
      connectingIccid,
      authStatus,
      startScan,
      stopScan,
      connectByIccid,
      disconnect,
      isConnected,
      isConnectedToSite,
      isSiteAdvertising,
      sendRelayCommand,
    ],
  );

  return <BluetoothContext.Provider value={value}>{children}</BluetoothContext.Provider>;
};

export default BluetoothProvider;

/**
 * Access BLE scan / session state and actions from within a BluetoothProvider.
 *
 * @returns {{
 *   isScanning: boolean,
 *   discoveredUnits: Map<string, {deviceId: string, rssi: number, name: string|null}>,
 *   connectedIccid: string|null,
 *   connectingIccid: string|null,
 *   authStatus: 'idle'|'connecting'|'authenticating'|'authenticated'|'failed',
 *   startScan: () => Promise<void>,
 *   stopScan: () => Promise<void>,
 *   connectByIccid: (iccid: string) => Promise<void>,
 *   disconnect: () => Promise<void>,
 *   isConnected: (iccid: string) => boolean,
 *   isConnectedToSite: (siteIccid: string|null|undefined) => boolean,
 *   isSiteAdvertising: (siteIccid: string|null|undefined) => boolean,
 *   sendRelayCommand: (relayNumber: number) => Promise<{success: boolean, error?: string}>,
 * }}
 * @throws {Error} If used outside a BluetoothProvider.
 */
export function useBluetooth() {
  const context = useContext(BluetoothContext);
  if (context === undefined) {
    throw new Error('useBluetooth must be used within a BluetoothProvider');
  }
  return context;
}
